import { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import TenantContext from '../../Context/TenantContext.jsx';

const PatientRegistrationAIAgent = () => {
  const navigate = useNavigate();
  const { tenant = { name: 'Smart Nurse', elevenLabs: {} } } = useContext(TenantContext) || {};
  const [widgetReady, setWidgetReady] = useState(false);

  const handleClose = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  useEffect(() => {
    const scriptId = 'elevenlabs-convai-embed';
    const existing = document.getElementById(scriptId);
    const onReady = () => setWidgetReady(true);

    if (existing) {
      setTimeout(onReady, 300);
      return undefined;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = 'https://unpkg.com/@elevenlabs/convai-widget-embed';
    script.async = true;
    script.type = 'text/javascript';
    script.onload = onReady;
    script.onerror = () => setWidgetReady(false);
    document.body.appendChild(script);

    return () => {
      script.remove();
    };
  }, []);

  useEffect(() => {
    if (!widgetReady) return undefined;

    let attempts = 0;
    const maxAttempts = 12;
    const interval = setInterval(() => {
      attempts += 1;
      const el = document.querySelector('elevenlabs-convai');
      if (!el) return;
      try {
        el.setAttribute('expanded', 'true');
        el.setAttribute('open', 'true');
        el.setAttribute('mode', 'expanded');
        el.setAttribute('data-expanded', 'true');
        // @ts-ignore custom element properties
        el.expanded = true;
        // @ts-ignore custom element properties
        el.open = true;
        // @ts-ignore custom element API
        if (typeof el.open === 'function') el.open();
        // @ts-ignore custom element API
        if (typeof el.expand === 'function') el.expand();
        // @ts-ignore custom element API
        const sr = el.shadowRoot;
        if (sr) {
          const openBtn = sr.querySelector('button, [role="button"], .open, .toggle');
          if (openBtn && typeof openBtn.click === 'function') {
            openBtn.click();
          }
        }
      } catch (error) {
        // Ignore failures; retries will continue until attempts are exhausted
      }

      if (attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 250);

    return () => clearInterval(interval);
  }, [widgetReady]);

  // Inject custom styles to make widget look native
  useEffect(() => {
    const styleId = 'elevenlabs-native-styles';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .elevenlabs-widget-container {
        position: relative !important;
        z-index: 10 !important;
      }
      elevenlabs-convai {
        display: block !important;
        width: 100% !important;
        height: 100% !important;
        border: none !important;
        background: white !important;
        box-shadow: none !important;
        position: relative !important;
        z-index: 10 !important;
      }
      elevenlabs-convai::part(widget),
      elevenlabs-convai::part(container),
      elevenlabs-convai::part(dialog) {
        border-radius: 0 !important;
        width: 100% !important;
        height: 100% !important;
        max-width: 100% !important;
        max-height: 100% !important;
        border: none !important;
        box-shadow: none !important;
        background: white !important;
        margin: 0 !important;
        padding: 0 !important;
        position: relative !important;
        z-index: 10 !important;
      }
      elevenlabs-convai,
      elevenlabs-convai > *,
      elevenlabs-convai > * > * {
        position: relative !important;
        z-index: 10 !important;
        box-shadow: none !important;
      }
      elevenlabs-convai [style*="position: fixed"],
      elevenlabs-convai [style*="position:fixed"] {
        position: relative !important;
        z-index: 10 !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) existingStyle.remove();
    };
  }, []);

  const agentId = import.meta.env.VITE_ELEVENLABS_PATIENT_REGISTRATION_AGENT_ID || 'agent_2001knw264rqe8hb3zgdgpm3wx6q';

  return (
    <div className="bg-white min-h-screen">
      <main className="pt-4 px-4 pb-4 bg-white">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-white to-slate-300 flex items-center justify-center">
                  <span className="text-lg">📋</span>
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white animate-pulse" />
              </div>
              <div>
                <h1 className="text-base font-semibold text-gray-900">Patient Registration</h1>
                <p className="text-xs text-gray-500">{tenant?.name || 'Vikram Aura Hospitals'}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleClose}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-700 transition-colors duration-200 active:scale-95 hover:bg-red-600 hover:text-white"
                aria-label="Close Patient Registration Agent"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Widget Content Area */}
          <div className="bg-white rounded-lg overflow-hidden elevenlabs-widget-container" style={{ height: 'calc(100vh - 180px)', minHeight: '500px', position: 'relative', zIndex: 10 }}>
            {widgetReady ? (
              <div className="w-full h-full" style={{ position: 'relative', zIndex: 10 }}>
                {/* eslint-disable-next-line react/no-unknown-property */}
                <elevenlabs-convai
                  agent-id={agentId}
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'block',
                    background: 'white',
                    border: 'none',
                    boxShadow: 'none',
                    position: 'relative',
                    zIndex: 10,
                  }}
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full gap-6 p-6 bg-white">
                <div className="relative">
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg animate-pulse">
                    <span className="text-3xl md:text-4xl">📋</span>
                  </div>
                  <div className="absolute inset-0 rounded-full border-2 border-blue-400/50 animate-ping" style={{ animationDuration: '1.5s' }} />
                  <div className="absolute inset-0 rounded-full border border-indigo-400/30 animate-ping" style={{ animationDuration: '2s', animationDelay: '0.5s' }} />
                </div>

                <div className="text-center space-y-2">
                  <h2 className="text-xl md:text-2xl font-semibold text-gray-900">
                    Connecting to Patient Registration
                  </h2>
                  <p className="text-sm md:text-base text-gray-500">
                    {tenant?.name || 'Vikram Aura Hospitals'} • New Patient Intake
                  </p>
                </div>

                <div className="w-48 md:w-64 h-1 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-blue-500 via-indigo-600 to-blue-500 rounded-full"
                    style={{
                      width: '40%',
                      animation: 'loadingBar 1.5s ease-in-out infinite',
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <style>{`
        @keyframes loadingBar {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(150%); }
          100% { transform: translateX(-100%); }
        }
      `}</style>
    </div>
  );
};

export default PatientRegistrationAIAgent;
